import type { FC } from 'react'
import { ChevronRight } from 'lucide-react'
import type { CriticalEvent } from '../core/dashboard-types'
import CardInfoButton from './CardInfoButton'

const criticalEventsMeta = {
  description: 'Upcoming audits, surveys, dry-docks, and regulatory deadlines requiring attention across the fleet.',
  endpoint: 'GET /events/upcoming-critical?companyId={cgaId}',
  logic: 'Fetches calendar events marked HIGH or MEDIUM criticality. Calculates days until each event; those due within 30 days are flagged HIGH severity.',
}

type Props = {
  events: CriticalEvent[]
  onViewAll?: () => void
  onEventClick?: (event: CriticalEvent) => void
}

const CriticalEvents: FC<Props> = ({ events, onViewAll, onEventClick }) => {
  return (
    <div className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex-1'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-bold text-slate-900'>Upcoming Critical Events</h3>
          <CardInfoButton meta={criticalEventsMeta} />
        </div>
        <button className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5' onClick={onViewAll}>
          View all <ChevronRight className='w-3 h-3' />
        </button>
      </div>
      <div className='space-y-3'>
        {events.length > 0 ? events.map((event, idx) => {
          const isHigh = event.severity === 'HIGH'
          const isLast = idx === events.length - 1

          return (
            <div
              key={event.id}
              className={`flex items-start gap-3 ${!isLast ? 'pb-3 border-b border-slate-50' : ''} cursor-pointer hover:bg-slate-50 rounded-lg transition-colors px-1`}
              onClick={() => onEventClick?.(event)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onEventClick?.(event) }}
            >
              <div className='flex-shrink-0 w-10 text-center'>
                <div className={`text-lg font-bold ${isHigh ? 'text-red-600' : 'text-amber-600'} leading-none`}>
                  {event.day}
                </div>
                <div className={`text-[10px] font-medium ${isHigh ? 'text-red-500' : 'text-amber-500'}`}>
                  {event.month}
                </div>
              </div>
              <div className='flex-1 min-w-0'>
                <div className='text-xs font-semibold text-slate-800'>{event.title}</div>
                <div className='text-[11px] text-slate-500'>{event.vessel}</div>
              </div>
              <div className='text-right flex-shrink-0'>
                <div className='text-[11px] text-slate-500'>{event.daysLabel}</div>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    isHigh ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50'
                  }`}
                >
                  {event.severity}
                </span>
              </div>
            </div>
          )
        }) : (
          <div className='py-10 text-center text-slate-400 text-xs'>No upcoming critical events</div>
        )}
      </div>
    </div>
  )
}

export default CriticalEvents
